import React, { useState } from 'react';
import { Typography, Button, Grid } from 'antd';
import { FileTextOutlined, FilePdfOutlined, FileWordOutlined, FileImageOutlined, DatabaseOutlined, DownOutlined, UpOutlined, AudioOutlined, ExclamationCircleOutlined, FormOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import FilePreviewDrawer from './FilePreviewDrawer';
import TextPreviewDrawer from './TextPreviewDrawer';
import SourceThumb from './SourceThumb';

const { Text } = Typography;
const { useBreakpoint } = Grid;

// Constants for icon styling
const ICON_STYLE = {
  fontSize: 28,
  color: '#6c757d'
};

// Helper function to get valid files
function getValidFiles(files) {
  return files.filter(
    (file) => !file.hasProblem() && file.getDocTranscript() && file.getDocTranscript().trim() !== ''
  );
}

const NoteSourcesGallery = ({ files = [], source }) => {
  const { t } = useTranslation();
  const [isExpanded, setIsExpanded] = useState(false);
  const [activeDrawer, setActiveDrawer] = useState(null); // 'file', 'transcript', 'userNotes', or null
  const [previewFile, setPreviewFile] = useState(null);
  const screens = useBreakpoint();
  const isMobile = !screens.md;

  const getFileIcon = (fileName) => {
    const extension = fileName.split('.').pop().toLowerCase();
    switch (extension) {
      case 'pdf':
        return <FilePdfOutlined style={ICON_STYLE} />;
      case 'docx':
        return <FileWordOutlined style={ICON_STYLE} />;
      case 'jpg':
      case 'jpeg':
      case 'png':
      case 'gif':
      case 'webp':
      case 'heic':
        return <FileImageOutlined style={ICON_STYLE} />;
      default:
        return <FileTextOutlined style={ICON_STYLE} />;
    }
  };

  const getTranscriptIcon = () => {
    return <AudioOutlined style={ICON_STYLE} />;
  };

  const getUserNotesIcon = () => {
    return <FormOutlined style={ICON_STYLE} />;
  };

  const getTranscriptLabel = () => {
    return t('session_files.recording');
  };

  const getUserNotesLabel = () => {
    return t('session_files.user_notes');
  };

  const getFileLabel = (file) => {
    return file.getFileName();
  };

  const handleFileClick = (file) => {
    setPreviewFile(file);
    setActiveDrawer('file');
  };

  const handleCloseDrawer = () => {
    setActiveDrawer(null);
    setPreviewFile(null);
  };

  const handleOpenFullFile = () => {
    if (previewFile) {
      window.open(previewFile.getUrl(), '_blank');
    }
  };

  const handleTranscriptClick = () => {
    setActiveDrawer('transcript');
  };

  const handleUserNotesClick = () => {
    setActiveDrawer('userNotes');
  };

  // Check for transcript - only show if present and non-blank
  const transcript = source.getTranscript();
  const hasValidTranscription = transcript && typeof transcript === 'string' && transcript.trim() !== '';

  // Check for user notes
  const userNotes = source.getUserNotes();
  const hasUserNotes = userNotes && typeof userNotes === 'string' && userNotes.trim() !== '';

  // Only count non-problematic files
  const validFiles = getValidFiles(files);
  const totalSources = (hasValidTranscription ? 1 : 0) + (hasUserNotes ? 1 : 0) + validFiles.length;

  // Show cue only if any file has a problem (not transcript issues)
  const showProblemCue = files.length > validFiles.length;

  // Remove hasFiles and hasTranscription, use validFiles.length and hasValidTranscription directly
  if (validFiles.length === 0 && !hasValidTranscription && !hasUserNotes) return null;

  return (
    <>
      <div style={{ 
        marginBottom: '20px', 
        padding: '10px', 
        backgroundColor: '#f8f9fa', 
        borderRadius: '8px',
        border: '1px solid #e9ecef'
      }}>
        <div 
          data-testid="sources-header"
          style={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'space-between',
            marginBottom: isExpanded ? '16px' : '0',
            cursor: 'pointer',
            padding: '4px',
            borderRadius: '4px',
            transition: 'background-color 0.2s ease',
            ':hover': {
              backgroundColor: '#f0f0f0'
            }
          }}
          onClick={() => setIsExpanded(!isExpanded)}
        >
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '8px',
            fontSize: '14px'
          }}>
            <Text style={{ color: '#333' }}>
              {isMobile ? t('session_files.sources_used_mobile') : t('session_files.sources_used')}
              <span> ({totalSources})</span>
            </Text>
            {/* Visual cue for problems in collapsed state */}
            {!isExpanded && showProblemCue && (
              <ExclamationCircleOutlined style={{ color: '#ff4d4f', fontSize: 15 }} title={t('session_files.problem_cue_tooltip') || 'Some sources have issues'} />
            )}
          </div>
          <Button 
            data-testid="sources-expand-btn"
            type="text" 
            size="small"
            icon={isExpanded ? <UpOutlined /> : <DownOutlined />}
            style={{ padding: '4px 8px' }}
            onClick={(e) => {
              e.stopPropagation(); // Prevent double triggering
              setIsExpanded(!isExpanded);
            }}
          >
          </Button>
        </div>
        
        {isExpanded && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
            {/* Recording thumbnail - always first */}
            {hasValidTranscription && (
              <div data-testid="transcript-thumb">
                <SourceThumb
                  onClick={handleTranscriptClick}
                  icon={getTranscriptIcon()}
                  label={getTranscriptLabel()}
                  valid={hasValidTranscription}
                />
              </div>
            )}

            {/* User notes thumbnail - after transcript */}
            {hasUserNotes && (
              <div data-testid="user-notes-thumb">
                <SourceThumb
                  onClick={handleUserNotesClick}
                  icon={getUserNotesIcon()}
                  label={getUserNotesLabel()}
                  valid={true}
                />
              </div>
            )}

            {/* File thumbnails: show all files, not just valid ones */}
            {files.map((file) => (
              <div data-testid={`file-thumb-${file.getUUID()}`} key={file.getUUID()}>
                <SourceThumb
                  onClick={() => handleFileClick(file)}
                  icon={getFileIcon(file.getFileName())}
                  label={getFileLabel(file)}
                  valid={file.isValid()}
                />
              </div>
            ))}
          </div>
        )}
      </div>

      {/* File Preview Drawer */}
      {activeDrawer === 'file' && (
        <FilePreviewDrawer
          file={previewFile}
          visible={true}
          onClose={handleCloseDrawer}
          onOpenFullFile={handleOpenFullFile}
        />
      )}

      {/* Transcript Drawer */}
      {activeDrawer === 'transcript' && (
        <TextPreviewDrawer
          content={source.getTranscript()}
          title={t('session_files.recording')}
          noContentMessage={t('session_files.no_transcript_available')}
          visible={true}
          onClose={handleCloseDrawer}
        />
      )}

      {/* User Notes Drawer */}
      {activeDrawer === 'userNotes' && (
        <TextPreviewDrawer
          content={source.getUserNotes()}
          title={t('session_files.user_notes')}
          noContentMessage={t('session_files.no_user_notes_available')}
          visible={true}
          onClose={handleCloseDrawer}
        />
      )}
    </>
  );
};

export default NoteSourcesGallery; 